/** Mobile-safe export downloader.
 *
 * Strategy:
 *   1. fetch() the file as a Blob with proper auth (?auth=token) and force MIME
 *      via new Blob([bytes], {type}) — Android Chrome strips MIME on anonymous
 *      blobs which breaks the `download` attribute.
 *   2. Try the standard anchor.click() download path.
 *   3. Always return the blobUrl + Blob so callers can offer fallback UI
 *      (Open in new tab / Web Share / long-press to save) when mobile browsers
 *      silently drop the auto-download.
 *   4. Auto-revoke after 60s so memory is freed even if the user did nothing.
 */
import { API } from "@/lib/api";

export function exportUrl(path) {
    const token = localStorage.getItem("aivos_token") || "";
    const sep = path.includes("?") ? "&" : "?";
    return `${API}${path}${sep}auth=${encodeURIComponent(token)}`;
}

const MIME_FROM_EXT = {
    pdf: "application/pdf",
    csv: "text/csv;charset=utf-8",
    txt: "text/plain;charset=utf-8",
    json: "application/json",
};

function mimeFor(filename) {
    const ext = (filename.split(".").pop() || "").toLowerCase();
    return MIME_FROM_EXT[ext] || "application/octet-stream";
}

export function isMobile() {
    if (typeof navigator === "undefined") return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent || "",
    );
}

export function isIOS() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    // iPadOS 13+ identifies as Mac — detect via touch points
    return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

/** Fetch + wrap into a Blob URL. Throws on HTTP errors with backend `detail`. */
export async function prepareDownload(path, filename) {
    const url = exportUrl(path);
    const res = await fetch(url, {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
    });
    if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
            const text = await res.text();
            const parsed = JSON.parse(text);
            detail = parsed.detail || detail;
        } catch { /* not JSON */ }
        throw new Error(detail);
    }
    const rawBlob = await res.blob();
    const type = mimeFor(filename);
    const blob = new Blob([rawBlob], { type });
    const blobUrl = URL.createObjectURL(blob);
    // Auto-revoke after 60s so the user has time to tap fallback actions
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return { blob, blobUrl, filename, type };
}

/** Anchor-based "save as" attempt. Works on desktop + most modern Android Chrome. */
export function triggerSave(blobUrl, filename) {
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = blobUrl;
    a.download = filename || "download";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
}

/** Open the blob in a new tab/window (iOS-friendly). */
export function openInNewTab(blobUrl) {
    const w = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (!w || w.closed || typeof w.closed === "undefined") {
        // Popup blocked: navigate same tab as last resort
        window.location.href = blobUrl;
    }
}

/** Share via Web Share API. Returns true if a share sheet was successfully shown. */
export async function shareFile(blob, filename) {
    if (typeof navigator === "undefined" || !navigator.share || !navigator.canShare) return false;
    try {
        const file = new File([blob], filename, { type: blob.type });
        if (!navigator.canShare({ files: [file] })) return false;
        await navigator.share({ files: [file], title: filename });
        return true;
    } catch (_e) {
        return false; // user cancelled or sharing failed
    }
}

/** Backwards-compatible wrapper: prepare + trigger save + return ok flag. */
export async function downloadExport(path, filename, onError) {
    try {
        const { blobUrl } = await prepareDownload(path, filename);
        triggerSave(blobUrl, filename);
        return true;
    } catch (err) {
        onError && onError(err);
        return false;
    }
}
